/**
 * @deprecated Use `IntegerInput` from `@krea/shared/design-system/input/integer-input` combined with
 * `ControlledWrapper` from `@krea/shared/design-system/input/controlled-wrapper` for React Hook Form integration.
 */
import type {
  ChangeEvent,
  InputHTMLAttributes,
  PropsWithChildren,
  ReactNode,
} from "react";
import type { Control, FieldValues, Path } from "react-hook-form";

import { cn } from "@krea/shared/utils";

import { formatToNumber } from "../../utils/formatNumber";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  useFormField,
} from "../Form";
import { Input } from "../inputs/Input";

interface INtegerInputProps<T extends FieldValues>
  extends Pick<
    InputHTMLAttributes<HTMLInputElement>,
    "className" | "placeholder"
  > {
  name: Path<T>;
  label?: string;
  control: Control<T>;
  required?: boolean;
  rightText?: ReactNode | string;
  acceptsDecimals?: boolean;
  stepsDisabled?: boolean;
}

function IntegerInputControl({
  value,
  onChange,
  restField,
  rest,
  invalid,
  isSubmitting,
  rightText,
  acceptsDecimals,
  stepsDisabled,
}: {
  value: string | number | undefined;
  onChange: (v: number | undefined) => void;
  restField: Record<string, unknown>;
  rest: Record<string, unknown>;
  invalid: boolean;
  isSubmitting: boolean;
  rightText?: ReactNode | string;
  acceptsDecimals: boolean;
  stepsDisabled: boolean;
}) {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-call -- useFormField is typed in Form.tsx
  const { formItemId } = useFormField();

  const inputProps = {
    ...restField,
    value: value ?? "",
    disabled: isSubmitting,
    id: formItemId,
    type: "number" as const,
    step: "0.01",
    min: "0",
    error: invalid ? " " : undefined,
    variant: invalid ? ("error" as const) : ("default" as const),
    onChange: (e: ChangeEvent<HTMLInputElement>) => {
      const num = formatToNumber(
        e.target.value,
        acceptsDecimals ? "float" : "integer",
      );
      onChange(num ?? undefined);
    },
  };

  return (
    <div className="flex w-full flex-col">
      <div className="flex w-full">
        {rightText ? (
          <>
            <Input
              {...inputProps}
              className={cn(
                "flex-1 min-w-0 rounded-r-none",
                stepsDisabled &&
                  "[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none",
              )}
              {...rest}
            />
            <div
              className={cn(
                "pointer-events-none flex h-10 shrink-0 items-center justify-center rounded-r-md border border-gray-200 border-l-0 bg-gray-50 px-3 text-sm font-semibold text-black",
                invalid && "!border-red-500",
                isSubmitting && "opacity-50",
              )}
            >
              {rightText}
            </div>
          </>
        ) : (
          <Input
            {...inputProps}
            className={
              stepsDisabled
                ? "[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                : ""
            }
            {...rest}
          />
        )}
      </div>
      <FormMessage />
    </div>
  );
}

export const ControlledIntegerInput = <T extends FieldValues>({
  name,
  control,
  required = false,
  rightText,
  label,
  acceptsDecimals = true,
  stepsDisabled = false,
  ...rest
}: PropsWithChildren<INtegerInputProps<T>>) => {
  return (
    <FormField
      control={control}
      name={name}
      render={({
        field: { value, onChange, ...restField },
        fieldState: { invalid },
        formState: { isSubmitting },
      }) => (
        <FormItem>
          {label && (
            <FormLabel className="font-bold">
              {label} {required && <span className="text-red-300">*</span>}
            </FormLabel>
          )}
          <FormControl>
            <IntegerInputControl
              value={value}
              onChange={onChange}
              restField={restField}
              rest={rest}
              invalid={invalid}
              isSubmitting={isSubmitting}
              rightText={rightText}
              acceptsDecimals={acceptsDecimals}
              stepsDisabled={stepsDisabled}
            />
          </FormControl>
        </FormItem>
      )}
    />
  );
};
